import React, { useState, useEffect } from 'react'
import Modal from './Modal'
import Button from './Button'
import Input from './Input'
import ConfigValueInput from './ConfigValueInput'
import HighlightedPreview from './HighlightedPreview'
import api from '../../utils/api'
import { toast } from '../toast/CustomToast'
import { detectCircularReferences, processTemplate, extractVariables } from '../../utils/configVariables'

const CompanyConfigsModal = ({ isOpen, onClose, company }) => {
    const [configs, setConfigs] = useState([])
    const [loading, setLoading] = useState(false)
    const [isFormOpen, setIsFormOpen] = useState(false)
    const [editingConfig, setEditingConfig] = useState(null)
    const [formData, setFormData] = useState({ key: '', value: '' })
    const [formErrors, setFormErrors] = useState({})
    const [isSaving, setIsSaving] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')

    useEffect(() => {
        if (isOpen && company) {
            fetchConfigs()
        }
    }, [isOpen, company])

    const fetchConfigs = async () => {
        setLoading(true)
        try {
            const response = await api.get(`/admin/companies/${company.id}/configs`)
            setConfigs(response.data.items || [])
        } catch (error) {
            toast.error('Erro ao carregar configurações: ' + (error.response?.data?.detail || error.message))
        } finally {
            setLoading(false)
        }
    }

    const validateForm = () => {
        const errors = {}
        if (!formData.key.trim()) errors.key = 'Chave é obrigatória'
        if (!formData.value.trim()) errors.value = 'Valor é obrigatório'

        // Validar chave duplicada (apenas se for nova ou se mudou a chave)
        if (!editingConfig || formData.key !== editingConfig.key) {
            const isDuplicate = configs.some(c => c.key === formData.key.trim())
            if (isDuplicate) errors.key = 'Esta chave já existe'
        }

        // Validar referências circulares
        const tempConfigs = editingConfig
            ? configs.map(c => c.id === editingConfig.id ? { key: formData.key, value: formData.value } : { key: c.key, value: c.value })
            : [...configs.map(c => ({ key: c.key, value: c.value })), { key: formData.key, value: formData.value }]

        const circularRefs = detectCircularReferences(tempConfigs)
        if (circularRefs.length > 0) {
            const [from, to] = circularRefs[0]
            errors.value = `Referência circular detectada: ${from} → ${to}`
        }

        setFormErrors(errors)
        return Object.keys(errors).length === 0
    }

    const handleSave = async () => {
        if (!validateForm()) return

        setIsSaving(true)
        try {
            // Prepara os dados: combina configs existentes com o novo/editado
            const allConfigs = editingConfig
                ? configs.map(c => c.id === editingConfig.id
                    ? { key: formData.key.trim(), value: formData.value }
                    : { key: c.key, value: c.value }
                  )
                : [...configs.map(c => ({ key: c.key, value: c.value })), { key: formData.key.trim(), value: formData.value }]

            // Usa o endpoint PUT que faz bulk save
            await api.put(
                `/admin/companies/${company.id}/configs`,
                { configs: allConfigs }
            )

            toast.success(editingConfig ? 'Configuração atualizada com sucesso' : 'Configuração criada com sucesso')
            setIsFormOpen(false)
            setEditingConfig(null)
            setFormData({ key: '', value: '' })
            setFormErrors({})
            fetchConfigs()
        } catch (error) {
            const errorMsg = error.response?.data?.detail || 'Erro ao salvar configuração'
            toast.error(errorMsg)
        } finally {
            setIsSaving(false)
        }
    }

    const handleDelete = (config) => {
        toast.confirm(
            'Tem certeza que deseja remover esta configuração?',
            async () => {
                try {
                    // Prepara os dados: remove a config selecionada
                    const allConfigs = configs
                        .filter(c => c.id !== config.id)
                        .map(c => ({ key: c.key, value: c.value }))

                    // Usa o endpoint PUT que faz bulk save
                    await api.put(
                        `/admin/companies/${company.id}/configs`,
                        { configs: allConfigs }
                    )

                    toast.success('Configuração removida com sucesso')
                    fetchConfigs()
                } catch (error) {
                    toast.error('Erro ao remover configuração')
                }
            }
        )
    }

    const handleEdit = (config) => {
        setEditingConfig(config)
        setFormData({ key: config.key, value: config.value })
        setFormErrors({})
        setIsFormOpen(true)
    }

    const handleNewConfig = () => {
        setEditingConfig(null)
        setFormData({ key: '', value: '' })
        setFormErrors({})
        setSearchTerm('')
        setIsFormOpen(true)
    }

    const handleCancel = () => {
        setIsFormOpen(false)
        setEditingConfig(null)
        setFormData({ key: '', value: '' })
        setFormErrors({})
    }

    const deleteIcon = <span className="material-icons sm">delete</span>
    const editIcon = <span className="material-icons sm">edit</span>
    const plusIcon = <span className="material-icons sm">add</span>
    const closeIcon = <span className="material-icons sm">close</span>
    const saveIcon = <span className="material-icons sm">save</span>
    const settingsIcon = <span className="material-icons sm">settings</span>

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={`Configurações - ${company?.razao_social || ''}`}
            loading={loading}
        >
            <div className="space-y-4">
                {!isFormOpen ? (
                    <>
                        {loading ? (
                            <div className="flex items-center justify-center h-64">
                                <div className="text-center">
                                    <span className="material-icons text-4xl text-blue-500 animate-spin block mb-2">refresh</span>
                                    <p className="text-gray-600 dark:text-gray-400">Carregando configurações...</p>
                                </div>
                            </div>
                        ) : configs.length === 0 ? (
                            <div className="text-center py-12 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-600">
                                <span className="material-icons text-4xl text-gray-300 block mb-2">tune</span>
                                <p className="text-gray-500 dark:text-gray-400 text-sm">Nenhuma configuração cadastrada</p>
                            </div>
                        ) : (
                            <div>
                                {configs.length > 0 && (
                                    <div className="mb-4 flex items-center gap-2">
                                        <div className="flex-1 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 flex items-center gap-2">
                                            <span className="material-icons text-gray-400 text-sm">search</span>
                                            <input
                                                type="text"
                                                placeholder="Filtrar configurações..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                className="flex-1 bg-transparent text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none text-sm"
                                            />
                                            {searchTerm && (
                                                <button
                                                    onClick={() => setSearchTerm('')}
                                                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition"
                                                >
                                                    <span className="material-icons sm">close</span>
                                                </button>
                                            )}
                                        </div>
                                        <Button
                                            size="sm"
                                            variant="primary"
                                            onClick={handleNewConfig}
                                        >
                                            {plusIcon} Nova
                                        </Button>
                                    </div>
                                )}

                                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                                    {configs
                                        .filter(c =>
                                            c.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                            c.value.toLowerCase().includes(searchTerm.toLowerCase())
                                        )
                                        .map((config) => (
                                    <div
                                        key={config.id}
                                        className="flex items-start justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 transition"
                                    >
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 break-all">
                                                {config.key}
                                            </p>
                                            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 break-all font-mono">
                                                {config.value}
                                            </p>
                                            {extractVariables(config.value).length > 0 && (
                                                <div className="mt-2 p-2 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded">
                                                    <p className="text-xs font-mono break-all">
                                                        <HighlightedPreview
                                                            originalValue={config.value}
                                                            processedValue={processTemplate(config.value, configs).processed}
                                                            configs={configs}
                                                        />
                                                    </p>
                                                </div>
                                            )}
                                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                                                {new Date(config.created_at).toLocaleDateString('pt-BR')}
                                            </p>
                                        </div>
                                        <div className="flex gap-2 ml-3 flex-shrink-0">
                                            <Button
                                                size="xs"
                                                variant="primary"
                                                onClick={() => handleEdit(config)}
                                                title="Editar"
                                            >
                                                {editIcon}
                                            </Button>
                                            <Button
                                                size="xs"
                                                variant="danger"
                                                onClick={() => handleDelete(config)}
                                                title="Remover"
                                            >
                                                {deleteIcon}
                                            </Button>
                                        </div>
                                    </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {configs.length === 0 && (
                            <div className="flex gap-2 pt-4">
                                <Button
                                    variant="primary"
                                    onClick={handleNewConfig}
                                    className="flex-1"
                                >
                                    {plusIcon} Nova Configuração
                                </Button>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="space-y-4">
                        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                            <p className="text-xs text-blue-700 dark:text-blue-300">
                                As configurações são armazenadas como pares chave/valor. Elas podem ser utilizadas para customizações da empresa.
                            </p>
                        </div>

                        <Input
                            label="Chave"
                            placeholder="ex: url_base, api_timeout"
                            value={formData.key}
                            onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                            error={formErrors.key}
                        />

                        <div>
                            <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                                Valor
                            </label>
                            <ConfigValueInput
                                value={formData.value}
                                onChange={(value) => setFormData({ ...formData, value })}
                                configs={configs}
                                error={formErrors.value}
                            />
                        </div>

                        <div className="flex gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                            <Button
                                variant="primary"
                                onClick={handleSave}
                                loading={isSaving}
                                disabled={isSaving}
                                className="flex-1"
                            >
                                {saveIcon} {editingConfig ? 'Atualizar' : 'Criar'}
                            </Button>
                            <Button
                                variant="secondary"
                                onClick={handleCancel}
                                disabled={isSaving}
                                className="flex-1"
                            >
                                {closeIcon} Cancelar
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    )
}

export default CompanyConfigsModal
